/**
 * views/projects/projectExport.js - Export CSV / Excel of what is on screen
 * (visible columns, filtered rows, the tree's order when grouped, the Won
 * totals) and the raw value the highlight rules compare. Moved verbatim out
 * of ProjectsView.jsx.
 */
import { leftoverOf, computeMargin } from '../../utils/projectMoney';
import { fxCode } from '../../components/ksa/ksaMoney';
import { exportCsv } from '../../utils/exportCsv';
import { projectStatus } from '../../components/projects/ActiveClosedPill';
import { exportViewRows } from '../../components/tableViews/exportView';
import { viewWorkbook, downloadXlsx } from '../../components/tableViews/xlsxExport';
import { exportLevelLook } from '../../components/tableViews/treeLook';
import { blankOr } from './tableBits';
import { csvMoney2, csvText } from '../../utils/csv';

export function makeProjectExport({ numericValueOf, COL_ACCESSORS, wonTotals, scope, tree, filtered, view, totalsCode }) {
  /* CSV export helpers — all $ values get 2 decimals; % rounded to whole;
     invoice numbers wrapped with ="..." so Excel preserves leading zeros. */
  const pctWhole = (v) => (v == null ? '' : `${Math.round(v * 100)}%`);

  // A column's value for the highlight rules: the number behind a money /
  // percent cell (null when blank), the date as stored, else the cell's text.
  const rawValue = (p, col) => {
    switch (col) {
      case 'quotedPrice':
        return blankOr(p.quotedPrice ?? p.quoted_price);
      case 'poValue':
        return blankOr(p.poValue ?? p.po_value);
      case 'invoicedAlready':
        return blankOr(p.invoicedAlready ?? p.invoiced_already);
      case 'moneyLeftover':
        return leftoverOf(p);
      case 'margin': {
        const m = computeMargin(p);
        return m == null ? null : m * 100;
      }
      case 'cogs':
      case 'profit':
        return numericValueOf(p, col);
      default:
        return COL_ACCESSORS[col] ? COL_ACCESSORS[col](p) : null;
    }
  };

  // Export = what is on screen (10/9/26): the visible columns in their order,
  // the filtered and sorted rows, and the Won totals row when it shows.
  const exportValue = (p, col) => {
    const cogs = (Number(p.quotedLaborCost) || 0) + (Number(p.quotedExpenses) || 0);
    switch (col) {
      case 'quoteDate':
        return p.quoteDate || '';
      case 'customer':
        return p.customer || '';
      case 'subCustomer':
        return p.subCustomer || '';
      case 'description':
        return p.description || p.name || '';
      case 'engineer':
        return p.engineer || '';
      case 'approvalManager':
        return p.approvalManager || p.approval_manager || '';
      // r49 tracking: the quote, its revision and the Global Project Number, as on the table
      case 'quoteLetter':
        return csvText(p.quoteLetter || p.quote_letter);
      case 'rev':
        return p.rev || '';
      case 'poDateIssued':
        return p.poDateIssued || p.po_date_issued || '';
      case 'poNumber':
        return csvText(p.poNumber || p.po_number);
      case 'terms':
        return p.terms || '';
      case 'poValue':
        return csvMoney2(p.poValue ?? p.po_value);
      case 'globalProjectNumber':
        return csvText(p.globalProjectNumber || p.global_project_number);
      case 'quotedPrice':
        return csvMoney2(p.quotedPrice);
      case 'cogs':
        return csvMoney2(cogs);
      case 'profit':
        return csvMoney2((Number(p.quotedPrice) || 0) - cogs);
      case 'margin':
        return pctWhole(computeMargin(p));
      case 'latestInvoiceDate':
        return p.latestInvoiceDate || p.latest_invoice_date || '';
      case 'invoiceNumbers':
        return csvText(p.invoiceNumbers || p.invoice_numbers);
      case 'invoicedAlready':
        return csvMoney2(p.invoicedAlready ?? p.invoiced_already);
      case 'moneyLeftover':
        return csvMoney2(leftoverOf(p));
      case 'status':
        return projectStatus(p); // Active / Closed, as the table shows
      default:
        return '';
    }
  };
  const exportTotals = () =>
    wonTotals.count > 0
      ? {
          quotedPrice: csvMoney2(wonTotals.sumPrice),
          cogs: csvMoney2(wonTotals.sumCogs),
          profit: csvMoney2(wonTotals.profit),
          margin: pctWhole(wonTotals.margin),
          invoicedAlready: csvMoney2(wonTotals.sumInvoiced),
          moneyLeftover: csvMoney2(wonTotals.sumLeftOver),
        }
      : null;
  const exportName = (ext) => `globalops_${scope ? 'ksa_' : ''}projects_${new Date().toISOString().slice(0, 10)}.${ext}`;
  // Grouped: the records flat in the tree's order, with a Main project column
  // first - a group row is never a row of the export, so nothing is summed twice
  const MAIN_COL = { col: 'mainProject', label: 'Main project', kind: 'text', minWidth: 160 };
  const exportRows =
    tree.on && tree.nodes
      ? tree.nodes.flatMap(function leaves(n) {
          return n.type === 'row' ? [n.record] : n.children.flatMap(leaves);
        })
      : filtered;
  const exportCols = tree.on && tree.groupCount > 0 ? [MAIN_COL, ...view.visible] : view.visible; // only when something is grouped
  const exportCell = (p, col) => (col === 'mainProject' ? tree.mainLabel(p) : exportValue(p, col));
  const handleExportCsv = () => {
    exportCsv(
      exportName('csv'),
      exportViewRows(exportCols, exportRows, exportCell, { totals: exportTotals(), totalsLabel: `Won totals (${wonTotals.count} records)` })
    );
  };
  // The same rows as an .xlsx with the highlight colours (tableViews/xlsxExport.js).
  // KSA money carries each record's currency code, as on screen.
  const handleExportXlsx = () => {
    const { bytes } = viewWorkbook(
      exportCols !== view.visible ? { ...view, visible: exportCols } : view,
      exportRows,
      exportCell,
      (p, col) => (col === 'mainProject' ? tree.mainLabel(p) : rawValue(p, col)),
      {
        sheetName: scope ? 'KSA Projects' : 'Projects',
        totals: exportTotals(),
        totalsLabel: `Won totals (${wonTotals.count} records)`,
        currencyOf: (p) => (scope ? fxCode(p.currency) || 'SAR' : null),
        totalsCurrency: scope ? totalsCode : null,
        // a main project's / sub-project's own record carries its group row's colour (Customize > Group rows)
        rowLook: tree.on
          ? (p) => {
              const lv = tree.levelOf(p.id);
              return lv ? exportLevelLook(view.tree, lv) : null;
            }
          : null,
      }
    );
    downloadXlsx(exportName('xlsx'), bytes);
  };
  return { handleExportCsv, handleExportXlsx, rawValue };
}
